<template>
  <header
    v-if="seller || product"
    id="supply-header"
  >
    <VLink
      material="add"
      @click="showSupplyOfferCard()"
    >
      {{ addLinkText }}
    </VLink>
  </header>

  <VDataGridV2
    id="supply-grid"
    :class="$attrs.class"
    :loader="dataLoader"
    :paginated="isPaginated"
    :context-menu="SupplyOffersService.contextMenu"
    clickable
    layout="fill"
    @row:dblclick="showSupplyOfferCard"
  >
    <!-- Товар не показываем только внутри карточки товара -->
    <VDataGridColumnV2
      v-if="!product"
      id="product"
      width="200px"
      content-class="ellipsis"
      filter-send-name="product"
      filterable
      filter-type="select"
      filter-identity="*"
      :filter-props="{
          placeholder: 'Название товара',
          dataUrl:     'products.getList',
          searchable:  true,
          clearable:   true,
        }"
    >
      <template #label>
        Товар
        <div class="description">
          Артикул
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.product?.name"
        >
          <template v-if="row.product">{{ row.product.name }}</template>
          <template v-else>--</template>
        </div>
        <div class="description ellipsis">
          <template v-if="row.product?.code">{{ row.product.code }}</template>
          <template v-else>--</template>
        </div>
      </template>
    </VDataGridColumnV2>

    <!-- Партнёра не показываем только внутри карточки поставщика -->
    <VDataGridColumnV2
      v-if="!seller"
      id="seller"
      width="200px"
      content-class="ellipsis"
      filterable
      filter-identity="*"
      filter-type="select"
      filter-send-name="seller"
      :filter-props="{
          searchFunction: PartnersService.searchSellers,
          placeholder:    'Название партнера',
          clearable:      true,
        }"
    >
      <template #label>
        Партнер
        <div class="description">
          Юр. лицо
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.seller.name"
        >
          {{ row.seller.name }}
        </div>
        <div class="description ellipsis">
          {{ row.sellerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="manufacturer"
      width="180px"
      header-class="maxWidth100 ellipsis"
      content-class="ellipsis"
    >
      <template #label>
        Производитель
        <div class="description">
          Тип
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.manufacturer?.name"
        >
          <template v-if="row.manufacturer">{{ row.manufacturer.name }}</template>
          <template v-else>--</template>
        </div>
        <div class="description ellipsis">
          {{ SupplyOffer.getIsMainText(row.isMain) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="trade-mark"
        width="130px"
        heander-class="nowrap"
        content-class="ellipsis"
        label="Торговая марка"
    >
      <template #default="{ row }">
          {{ row.tradeMark?.name || '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        width="85px"
        content-align="center"
        label="Фасовка производителя"
    >
      <template #default="{ row }">
        {{ row.packageVolume?.formatSmart(row.unit) || '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        width="85px"
        label="Поставка"
        content-align="center"
    >
      <template #default="{ row }">
        <template v-if="row.deliveryDaysToStore !== null">
          {{ row.deliveryDaysToStore }} {{ $core.utils.regional.ru.pluralize(row.deliveryDaysToStore, 'день', 'дня', 'дней') }}
        </template>
        <template v-else>
          --
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="status"
      width="120px"
      hint="Статус доступности связки [поставщик - производитель]"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="statusId"
      :filter-props="{
          placeholder: 'Статус',
          options:     SupplyOffersService.getStatuses(),
          clearable:   true,
        }"
      @edit="openEditStatusPopup"
    >
      <template #label>
        Статус
        <div class="description">
          Доступность
        </div>
      </template>
      <template #default="{ row }">
        {{ row.status?.name || '--' }}
        <div
          class="description ellipsis"
          :title="row.statusAvailability?.name"
        >
          {{ row.statusAvailability?.name || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Кол-во"
      width="100px"
      content-align="right"
    >
      <template #default="{ row }">
        <div
          v-for="tariff in row.sortedTariffs"
          :key="tariff.id"
          class="tariff nowrap"
        >
          <template v-if="tariff.qty?.start != null">
            от {{ tariff.qty.start }} {{ row.unit }}
          </template>
          <template v-else>--</template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="price"
      label="Цена"
      width="140px"
      content-align="right"
    >
      <template #default="{ row }">
        <div
          v-for="tariff in row.sortedTariffs"
          :key="tariff.id"
          class="tariff nowrap"
        >
          {{ tariff.price.format(row.currency) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="tenders"
      label="Тендеры"
      width="90px"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <template v-if="row.isExcludedFromTenders">Исключен</template>
        <template v-else>Допущен</template>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2
      v-if="seller === null && product === null"
    >
      <VButton
        @click="showSupplyOfferCard()"
      >
        {{ addLinkText }}
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<style lang="scss" src="./SupplyOffersRegistry.scss" />

<script lang="ts">

import PartnersService     from '@/domains/Partners/PartnersService';
import SupplyOffer         from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOffersService from '@/domains/SupplyOffers/SupplyOffersService';
import DataGridLoader      from '@/lib/components/VDataGridV2/DataGridLoader';

import type Product from '@/domains/Products/Product';
import type Partner from '@/domains/Partners/Partner';

export default defineComponent({
  name: 'SupplyOffersRegistry',

  props: {
    /** Если передан товар — реестр открыт из карточки товара и фильтруется по нему */
    product: {
      type:    Object as PropType<Product>,
      default: null,
    },
    /** Если передан партнёр — реестр открыт из карточки поставщика и фильтруется по нему */
    seller: {
      type:    Object as PropType<Partner>,
      default: null,
    },
  },

  emits: [
    'update:product',
  ],

  setup() {
    return {
      PartnersService,
      SupplyOffer,
      SupplyOffersService,
    };
  },

  data() {
    return {
      dataLoader: this.createDataLoader(),
    };
  },

  computed: {
    /** Пагинация есть везде, кроме карточки товара — там список отдаётся наверх целиком */
    isPaginated(): boolean {
      if (this.product) {
        return false;
      }
      else {
        return true;
      }
    },

    addLinkText(): string {
      if (this.seller) {
        return 'Добавить товар';
      }
      else if (this.product) {
        return 'Добавить запись';
      }
      else {
        return 'Добавить разрешение';
      }
    },
  },

  methods: {
    /**
     * Три режима: из карточки товара, из карточки поставщика и плоский реестр
     * вне карточек. В режиме товара пагинации нет и загруженный список отдаётся
     * наверх, в остальных двух список постраничный.
     */
    createDataLoader(): DataGridLoader {
      /**
       * В постоянные параметры кладём только то, что действительно задано
       * контекстом. Иначе в плоском реестре product и seller уезжают со
       * значением null и конфликтуют с одноимёнными фильтрами колонок:
       * DataGridLoader берёт постоянный параметр, а фильтр отбрасывает.
       */
      const extraParams: { product?: any; seller?: any } = {};

      if (this.product) {
        extraParams.product = this.product;
      }

      if (this.seller) {
        extraParams.seller = this.seller;
      }

      const loader = new DataGridLoader(SupplyOffersService.getListWithActualTariffsPaginated)
        .useExtraParams(extraParams);

      if (this.product) {
        loader
          .withoutPagination()
          .onAfterLoad(() => {
            // Вешать эмит на событие релоада может быть решение не очень,
            // так как непонятно что из чего следует, но это решение требует минимальное количество кода
            const localProduct = this.$core.utils.cloneDeep(this.product);

            localProduct.supplyOffers = (this.dataLoader as unknown as DataGridLoader).data;
            this.$emit('update:product', localProduct);
          });
      }

      return loader;
    },

    /** Попап смены статуса рисуется от курсора, якорь ячейки ему не нужен. */
    async openEditStatusPopup(row: SupplyOffer) {
      const result = await SupplyOffersService.showStatusPopup(row);

      if (result) {
        this.dataLoader.refresh();
      }
    },

    /**
     * Существующее разрешение карточка загружает сама по id — данные всегда свежие.
     * Для нового передаём заготовку с контекстом реестра. В плоском реестре
     * контекста нет: оба поля пустые, пользователь заполняет их в карточке.
     */
    async showSupplyOfferCard(supplyOffer: SupplyOffer | undefined = undefined) {
      let cardProps;

      if (supplyOffer) {
        cardProps = { id: supplyOffer.id };
      }
      else {
        cardProps = { supplyOffer: SupplyOffer.fromSellerProduct(this.seller, this.product) };
      }

      if (await this.$core.dialogs.SupplyOfferCard.showAsPromise(cardProps)) {
        await this.dataLoader.refresh();
      }
    },
  },
});
</script>
